import React, { useMemo } from "react";
import { Field, getFormValues } from "redux-form";
import { useSelector } from "react-redux";
import { ReduxControls } from "rhombus-react";

import { ASSET_TYPES } from "utils/constants";
import { formatBytes, noop } from "utils/common";
import {
	mapStringToOption,
	dynamicMapStringToOption,
	dynamicNormalizeOptionToString, isRequiredAssetField
} from "social/utils";
import DynamicInput from "social/DynamicInput";
import DynamicSelect from "social/DynamicSelect";
import DynamicAssetSelect from "social/DynamicAssetSelect";
import { SNAPCHAT_STORY_CTAS } from "social/constants/CTA";
import { SNAPCHAT_CAROUSEL_CONSTRAINTS } from "social/validators/snapchat";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";
import { imposiumComposition } from "social/ImposiumCompSelector";
import { imposiumProject } from "social/ImposiumProjectSelector";

import "./CarouselCardForm.css";
import "./SnapchatCarouselStorySnapForm.css";


const {
	MAX_LENGTH_LANDING_PAGE,
	IMAGE_MAX_BYTES,
	IMAGE_DIMENSIONS,
	IMAGE_ACCEPT,
	MIN_VIDEO_SEC,
	MAX_VIDEO_MIN,
	VIDEO_MAX_BYTES,
	VIDEO_DIMENSIONS,
	VIDEO_ACCEPT,
} = SNAPCHAT_CAROUSEL_CONSTRAINTS;

export const SNAPCHAT_CAROUSEL_STORY_SNAP_FORM_FIELDS = {
	imposiumProject,
	imposiumComposition,
	video: {
		name: "video",
		label: "Video Asset",
		className: "full-width cypress-video-select",
		assetType: ASSET_TYPES.VIDEO,
		component: DynamicAssetSelect,
		required: true,
		accept: VIDEO_ACCEPT,
		requirements: [
			{
				name: "File Size",
				description: `Up to ${formatBytes(VIDEO_MAX_BYTES)}`,
			},
			{
				name: "File Types",
				description: VIDEO_ACCEPT,
			},
			{
				name: "Dimensions",
				description: `${VIDEO_DIMENSIONS.width}x${VIDEO_DIMENSIONS.height} pixels`,
			},
			{
				name: "Duration",
				description: `${MIN_VIDEO_SEC}s - ${MAX_VIDEO_MIN}m`,
			}
		],
	},
	image: {
		name: "image",
		label: "Image Asset",
		className: "full-width cypress-image-select",
		assetType: ASSET_TYPES.IMAGE,
		component: DynamicAssetSelect,
		required: true,
		accept: IMAGE_ACCEPT,
		requirements: [
			{
				name: "File Size",
				description: `Up to ${formatBytes(IMAGE_MAX_BYTES)}`,
			},
			{
				name: "File Types",
				description: IMAGE_ACCEPT,
			},
			{
				name: "Dimensions",
				description: `${IMAGE_DIMENSIONS.width}x${IMAGE_DIMENSIONS.height} pixels`,
			},
		],
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		component: DynamicInput,
		defaultDynVarName: "",
		className: "full-width",
		placeholder: "https://www.example.com",
		required: true,
		charMax: MAX_LENGTH_LANDING_PAGE,
	},
	cta: {
		name: "cta",
		label: "CTA",
		component: DynamicSelect,
		defaultDynVarName: "",
		className: "half-width",
		options: SNAPCHAT_STORY_CTAS.map(mapStringToOption),
		format: dynamicMapStringToOption,
		normalize: dynamicNormalizeOptionToString,
		onMenuScrollToBottom: noop,
		required: true,
		menuPlacement: "top",
		placeholder: "Select Call to Action Text",
		isClearable: false,
		variableNameInputTip: "Edit variable name",
	},
};

function fieldGenerator(props) {
	const {
		assetType,
		creativeAssetType,
		libraryId,
		cardPrefix,
		navigationActions,
		isViewOnly = false,
		showPristineErrors = false,
		doVersionsExist,
	} = props;


	return (fieldProps) => {
		let extraProps = {
			disabled: isViewOnly,
			showPristineErrors,
		};

		if(extraProps.disabled && fieldProps.placeholder){
			extraProps.placeholder = "";
		}

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
		}

		if(/image|video/gi.test(fieldProps.name)) {
			extraProps.libraryId = libraryId;
			extraProps.navigationActions = navigationActions;
			extraProps.creativeAssetType = creativeAssetType;
			extraProps.required = isRequiredAssetField(creativeAssetType);
		}

		if((assetType === "image" && fieldProps.label?.includes("Video"))
			|| (assetType === "video" && fieldProps.label?.includes("Image"))){
			return;
		}

		if(cardPrefix === "cards[0]" && name === "imposiumComposition"){
			extraProps.showProjectName = false;
		}

		return (
			<Field
				key={fieldProps.label}
				{...fieldProps}
				{...extraProps}
				name = {`${cardPrefix}.${fieldProps.name}`}
			/>
		);
	};
}

export default function SnapchatCarouselStorySnapsForm(props){
	const {
		libraryId,
		navigationActions,
		adType,
		isViewOnly,
		showPristineErrors,
		doVersionsExist,
	} = props;

	const values = useSelector(state => getFormValues(SOCIAL_WIZARD)(state)?.[adType] || {});

	const currentCard = values?.currentCard || 0;
	const assetType = values?.cards?.[currentCard]?.type;
	const creativeAssetType = values.assetType;
	const cardPrefix = `cards[${currentCard}]`;
	const typeFieldName = `${cardPrefix}.type`;

	const generateFields = fieldGenerator({
		adType,
		assetType,
		creativeAssetType,
		libraryId,
		cardPrefix,
		isViewOnly,
		showPristineErrors,
		navigationActions,
		doVersionsExist,
	});
	const fields = useMemo(() => Object.values(SNAPCHAT_CAROUSEL_STORY_SNAP_FORM_FIELDS)
		.map(generateFields), [assetType, creativeAssetType, currentCard]);

	return (
		<div className="carousel-card-form snapchat-carousel-story-snap-form">
			<div className="fields">
				<div className="asset-type-radio-container">
					<span className="asset-type-heading">Asset Type:</span>
					<div>
						<Field
							label="Image"
							type="radio"
							className="asset-type-radio cypress-image-radio"
							name={typeFieldName}
							value={"image"}
							component={ReduxControls.Checkbox}
							checked={assetType === "image"}
							disabled={isViewOnly || doVersionsExist}
						/>
					</div>
					<div>
						<Field
							label="Video"
							type="radio"
							className="asset-type-radio cypress-video-radio"
							name={typeFieldName}
							value={"video"}
							component={ReduxControls.Checkbox}
							checked={assetType === "video"}
							disabled={isViewOnly || doVersionsExist}
						/>
					</div>
				</div>
				{fields}
			</div>
		</div>
	);
}
